import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import { get, post, put, del } from '../../services/api';
import TimetableConfiguration from './TimetableConfiguration';
import TimetableGrid from './TimetableGrid';
import TimetableEntry from './TimetableEntry';
import TimetableHistory from './TimetableHistory';
import styles from './Timetable.module.css';
import axios from 'axios';

const Timetables = () => {
  // Auth & Organization
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId") === "null" ? null : localStorage.getItem("centerId");
  
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  // State Management
  const [activeTab, setActiveTab] = useState('view');
  const [configuration, setConfiguration] = useState(null);
  const [timetableData, setTimetableData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Dropdowns Data
  const [classesData, setClassesData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [teachersData, setTeachersData] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);
  
  // Refs
  const componentRef = useRef();

  // Print logic
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'Timetable',
  });

  // Selected Values
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [viewMode, setViewMode] = useState('class'); // class, teacher, all
const [showTimetable, setShowTimetable] = useState(false);
  // Load Configuration on mount
  useEffect(() => {
    loadConfiguration();
    fetchClasses();
    fetchTeachers();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await get(
        `Timetable/GetActiveConfiguration?centerId=${centerId}`,
        { headers }
      );
      setConfiguration(response);
    } catch (error) {
      console.error("Error loading configuration:", error);
      toast.error("Failed to load timetable configuration");
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await get(
        `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
        { headers }
      );
      setClassesData(response || []);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

const fetchSections = async (classId) => {
  try {
    if (!classId) {
      setSectionsData([]);
      return;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}ClassSection/GetAllSectionByCenterClassIdAndCenterIds`,
      {
        params: {
          centerClassId: classId,
          centerId: centerId,
        },
        headers, // your existing headers object
      }
    );

    setSectionsData(response.data || []);
  } catch (error) {
    console.error("Error fetching sections:", error);
  }
};
  const fetchTeachers = async () => {
    try {
      const response = await get(
        `UserAuthentication/GetUsersByCenterIdAndRoleId?centerId=${centerId}&roleId=7`,
        { headers }
      );
      setTeachersData(response || []);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    }
  };

  const fetchSubjects = async (classSectionId) => {
    try {
      if (!classSectionId) {
        setSubjectsData([]);
        return;
      }
      const response = await get(
        `StudentEvaluation/GetSubjectByClassSectionId?ClassSectionId=${classSectionId}`,
        { headers }
      );
      setSubjectsData(response.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const loadTimetable = async () => {
    try {
      setLoading(true);
      let params = `centerId=${centerId}`;
      
      if (viewMode === 'class' && selectedSection) {
        params += `&classSectionId=${selectedSection.value}`;
      } else if (viewMode === 'teacher' && selectedTeacher) {
        params += `&teacherId=${selectedTeacher.value}`;
      }
console.log("url",`Timetable/GetTimetable?${params}`)
      const response = await get(
        `Timetable/GetTimetable?${params}`,
        { headers }
      );
      
      setTimetableData(response);
      setShowTimetable(true);
    } catch (error) {
      console.error("Error loading timetable:", error);
      toast.error("Failed to load timetable");
    } finally {
      setLoading(false);
    }
  };

 const handleViewModeChange = (selected) => {
  console.log("selected",selected)
    setViewMode(selected.value);
    setShowTimetable(false); // Hide timetable when changing view mode
    setSelectedClass(null);
    setSelectedSection(null);
    setSelectedTeacher(null);
  };


  const handleClassChange = async (selected) => {
    setSelectedClass(selected);
    setSelectedSection(null);
    setSubjectsData([]);
    setShowTimetable(false);
    
    if (selected) {
      await fetchSections(selected.value);
    } else {
      setSectionsData([]);
    }
  };

  const handleSectionChange = async (selected) => {
    setSelectedSection(selected);
    setShowTimetable(false);
    if (selected) {
      await fetchSubjects(selected.value);
      if (viewMode === 'class') {
        await loadTimetable();
      }
    }
  };

  const handleTeacherChange = async (selected) => {
    setSelectedTeacher(selected);
    setShowTimetable(false);
    if (selected && viewMode === 'teacher') {
      await loadTimetable();
    }
  };

  const handleConfigurationSave = async (config) => {
  try {
    setLoading(true);
    
    // Prepare the payload with all required fields
    const payload = {
      id: 0,
      organizationId: parseInt(organizationId),
      centerId: parseInt(centerId),
      academicYear: config.academicYear,
      term: config.term,
      periodsPerDay: parseInt(config.periodsPerDay),
      daysPerWeek: parseInt(config.daysPerWeek),
      periodDuration: parseInt(config.periodDuration),
      schoolStartTime: config.schoolStartTime,
      schoolEndTime: config.schoolEndTime,
      isActive: true,
      effectiveFrom: new Date(config.effectiveFrom).toISOString(),
      effectiveTo: null,
      breaks: config.breaks?.map(b => ({
        id: 0,
        breakName: b.breakName,
        afterPeriod: parseInt(b.afterPeriod),
        duration: parseInt(b.duration),
        startTime: b.startTime,
        endTime: b.endTime
      })) || [],
      periods: [] // This will be auto-generated by backend
    };

    console.log('Sending payload:', payload); // Debug log

    const response = await post(
      'Timetable/CreateConfiguration',
      payload,
      { headers }
    );
    
    setConfiguration(response);
    toast.success("Configuration saved successfully");
    setActiveTab('view');
  } catch (error) {
    console.error("Error saving configuration:", error);
    toast.error(error.response?.data?.message || "Failed to save configuration");
  } finally {
    setLoading(false);
  }
};
  const handleEntryCreate = async (entryData) => {
    try {
      setLoading(true);
      const response = await post(
        'Timetable/CreateEntry',
        { ...entryData, configurationId: configuration.id },
        { headers }
      );
      toast.success("Entry added successfully");
      await loadTimetable();
      return response;
    } catch (error) {
      console.error("Error creating entry:", error);
      toast.error(error.response?.data?.message || "Failed to add entry");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleEntryUpdate = async (id, entryData) => {
    try {
      setLoading(true);
      const response = await put(
        `Timetable/UpdateEntry/${id}`,
        entryData,
        { headers }
      );
      toast.success("Entry updated successfully");
      await loadTimetable();
      return response;
    } catch (error) {
      console.error("Error updating entry:", error);
      toast.error("Failed to update entry");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleEntryDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) {
      return;
    }

    try {
      setLoading(true);
      await del(
        `Timetable/DeleteEntry/${id}`,
        { headers }
      );
      toast.success("Entry deleted successfully");
      await loadTimetable();
    } catch (error) {
      console.error("Error deleting entry:", error);
      toast.error("Failed to delete entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.timetableContainer}>
      <div className={styles.header}>
        <h2>Timetable Management</h2>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'view' ? styles.active : ''}`}
            onClick={() => setActiveTab('view')}
          >
            View Timetable
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'config' ? styles.active : ''}`}
            onClick={() => setActiveTab('config')}
          >
            Configuration
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'entry' ? styles.active : ''}`}
            onClick={() => setActiveTab('entry')}
          >
            Add Entry
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'history' ? styles.active : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>
      </div>

      {activeTab === 'view' && (
        <div className={styles.content}>
          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label>View Mode</label>
              <Select
                value={viewMode}
                onChange={handleViewModeChange}
                options={[
                  { value: 'class', label: 'By Class' },
                  { value: 'teacher', label: 'By Teacher' },
                  { value: 'all', label: 'All' }
                ]}
                className="timetable-select"
                classNamePrefix="react-select"
              />
            </div>

            {viewMode === 'class' && (
              <>
                <div className={styles.filterGroup}>
                  <label>Class</label>
                  <Select
                    value={selectedClass}
                    onChange={handleClassChange}
                    options={classesData.map(c => ({
                      value: c.id,
                      label: c.name
                    }))}
                    className="timetable-select"
                    classNamePrefix="react-select"
                    placeholder="Select Class"
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label>Section</label>
                  <Select
                    value={selectedSection}
                    onChange={handleSectionChange}
                    options={sectionsData.map(s => ({
                      value: s.id,
                      label: s.name
                    }))}
                    className="timetable-select"
                    classNamePrefix="react-select"
                    placeholder="Select Section"
                    isDisabled={!selectedClass}
                  />
                </div>
              </>
            )}

            {viewMode === 'teacher' && (
              <div className={styles.filterGroup}>
                <label>Teacher</label>
                <Select
                  value={selectedTeacher}
                  onChange={handleTeacherChange}
                  options={teachersData.map(t => ({
                    value: t.id,
                    label: t.firstName
                  }))}
                  className="timetable-select"
                  classNamePrefix="react-select"
                  placeholder="Select Teacher"
                />
              </div>
            )}

            <button
              className={styles.loadButton}
              onClick={loadTimetable}
              disabled={loading}
            >
              Load Timetable
            </button>

            {showTimetable && timetableData && (
              <button
                className={styles.printButton}
                onClick={handlePrint}
                disabled={loading}
              >
                🖨️ Print
              </button>
            )}
          </div>

          {showTimetable && timetableData && (
            <TimetableGrid
              ref={componentRef}
              data={timetableData}
              configuration={configuration}
              onEntryEdit={handleEntryUpdate}
              onEntryDelete={handleEntryDelete}
              onEntryCreate={handleEntryCreate}
            />
          )}
        </div>
      )}

      {activeTab === 'config' && (
        <TimetableConfiguration
          configuration={configuration}
          onSave={handleConfigurationSave}
        />
      )}

      {activeTab === 'entry' && configuration && (
        <TimetableEntry
          configuration={configuration}
          classesData={classesData}
          sectionsData={sectionsData}
          teachersData={teachersData}
          subjectsData={subjectsData}
          onClassChange={handleClassChange}
          onSectionChange={handleSectionChange}
          onSave={handleEntryCreate}
        />
      )}

      {activeTab === 'history' && configuration && (
        <TimetableHistory
          configurationId={configuration.id}
          headers={headers}
        />
      )}
    </div>
  );
};

export default Timetables;